import {
  title_case,
  get_verification_status_tone,
  get_verification_impact,
  format_log_timestamp,
  service_matches_verification_type,
  get_case_identifier
} from './onboarding_verification_common_helpers';

function get_service_id(service = {}) {
  return Number(service?.id || service?.service_id || 0);
}

function resolve_service_from_verification_type(services = [], verification_type = '') {
  const service_list = Array.isArray(services) ? services : [];
  const matched_service = service_list.find(function service_matches(service) {
    return service_matches_verification_type(service, verification_type);
  });

  return matched_service || {};
}

function check_matches_auto_service(check = {}, service_id = 0, verification_type = '') {
  if (check?.verification_mode !== 'auto') return false;
  if (auto_service_id_matches(check, service_id)) return true;

  return service_matches_verification_type(check?.service || {}, verification_type);
}

function auto_service_id_matches(check = {}, service_id = 0) {
  if (service_id <= 0) return false;

  return Number(check?.service_id || 0) === service_id;
}

function get_row_identifier(row = {}, fallback) {
  if (row?.run_id) {
    return row.run_id;
  }

  return fallback;
}

function find_matching_auto_check(checks = [], verification_type = '', service = {}) {
  const service_id = get_service_id(service);
  const matched_check = checks.find(function matches_check(check) {
    return check_matches_auto_service(check, service_id, verification_type);
  });

  return matched_check || null;
}

function build_auto_history_entry(case_item, row = {}, index = 0) {
  const tone = get_verification_status_tone(row?.status);
  const case_identifier = get_case_identifier(case_item);
  const row_identifier = get_row_identifier(row, index);

  return {
    id: `${case_identifier}-auto-ledger-${row_identifier}`,
    status: title_case(row?.status || 'Pending'),
    status_tone: tone,
    detail: get_verification_impact(tone),
    last_run: format_log_timestamp(row?.completed_at || row?.started_at),
    raw_log: row
  };
}

function build_auto_history(case_item, history_rows, verification_type) {
  return history_rows
    .filter(function matches_type(history_row) {
      return history_row?.verification_type === verification_type;
    })
    .map(function map_history(history_row, index) {
      return build_auto_history_entry(case_item, history_row, index);
    });
}

function update_matched_auto_check(context, matched_check, verification_type, row) {
  const auto = row?.auto;
  const tone = get_verification_status_tone(auto?.status);
  const history = build_auto_history(context.case_item, context.history_rows, verification_type);
  const base_history = get_history_list(matched_check.history);
  const impact = get_auto_impact(row, tone);

  matched_check.status = title_case(auto?.status || matched_check.status || 'Pending');
  matched_check.status_tone = tone;
  matched_check.impact = impact;
  matched_check.last_run = format_log_timestamp(auto?.completed_at || auto?.started_at);
  matched_check.raw_status = auto;
  matched_check.history = [...base_history, ...history];
  context.checks_by_history_key.set(`ledger:${verification_type}`, matched_check);
}

function get_history_list(history) {
  if (Array.isArray(history)) {
    return history;
  }

  return [];
}

function get_auto_impact(row, tone) {
  if (row?.overdue_auto) {
    return 'Overdue verification';
  }

  return get_verification_impact(tone);
}

function append_auto_snapshot_check(context, verification_type, row) {
  const auto = row?.auto;
  if (!auto) return;

  const service = resolve_service_from_verification_type(context.services, verification_type);
  const matched_check = find_matching_auto_check(context.checks, verification_type, service);
  if (!matched_check) return;

  update_matched_auto_check(context, matched_check, verification_type, row);
}

function dedupe_history_entries(entries) {
  return entries.filter(function unique_entry(entry, entry_index) {
    return entries.findIndex(function same_entry(candidate) {
      return candidate?.id === entry?.id;
    }) === entry_index;
  });
}

function append_auto_history_row(context, row, index) {
  const verification_type = String(row?.verification_type || '').trim();
  const history_key = `ledger:${verification_type}`;
  const matched_check = context.checks_by_history_key.get(history_key);
  const history_entry = build_auto_history_entry(context.case_item, row, index);

  if (matched_check) {
    matched_check.history = dedupe_history_entries([...matched_check.history, history_entry]);
    return;
  }

  const service = resolve_service_from_verification_type(context.services, verification_type);
  const configured_check = find_matching_auto_check(context.checks, verification_type, service);
  if (!configured_check) return;

  const base_history = get_history_list(configured_check.history);
  configured_check.history = [...base_history, history_entry];
}

function get_auto_history_rows(auto_history = []) {
  if (!Array.isArray(auto_history)) {
    return [];
  }

  return auto_history.filter(function auto_row(row) {
    return row?.mode === 'auto';
  });
}

function get_snapshot(auto_status_snapshot = {}) {
  return auto_status_snapshot && typeof auto_status_snapshot === 'object'
    ? auto_status_snapshot
    : {};
}

export function append_auto_ledger_checks(context, auto_status_snapshot = {}, auto_history = []) {
  const history_rows = get_auto_history_rows(auto_history);
  const snapshot = get_snapshot(auto_status_snapshot);
  const ledger_context = {
    checks: context.checks,
    checks_by_history_key: context.checks_by_history_key,
    case_item: context.case_item,
    history_rows,
    services: context.services
  };

  Object.entries(snapshot).forEach(function append_snapshot(entry) {
    append_auto_snapshot_check(ledger_context, entry[0], entry[1]);
  });

  history_rows.forEach(function append_history(row, index) {
    append_auto_history_row(ledger_context, row, index);
  });
}
